import moment from 'moment';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';

import { Datepicker } from '@xsolla/ui-calendar';
import { Checkbox, FieldGroup } from '@xsolla/ui-core';

import { banUser } from '../../../../api';
import { BanOptions } from '../../../../api/users/types';
import { useNotifications } from '../../../../hooks/useNotifications';
import { FlexParentBlock } from '../../../../styled';
import { FieldSelect, FieldTextarea, FormFooter } from '../../../ui';
import { cleanDate } from './mutators';
import { CheckboxWrapper, DateSeparator, HoursSelectWrapper } from './style';
import {
  BanUserFields,
  banUserFieldsValidator,
  hoursSelect,
  maxTextLength,
  timezoneSelect
} from './validator';

type Props = {
  banData: BanOptions | null;
  projectId: string;
  userId: string;
  onBan: (banData: BanOptions) => void;
  onClose: () => void;
};

export const ModalBanFields = ({
  banData,
  userId,
  projectId,
  onBan,
  onClose
}: Props): JSX.Element => {
  const intl = useIntl();
  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const banUserHandle = async (values: BanUserFields) => {
    const fullData = `${moment(values.date).format('YYYY-MM-DDT')}${values.hour}${values.timezone}`;
    const extraBanData = values.permanent ? undefined : fullData;

    try {
      setFetching(true);
      await banUser(projectId, userId, {
        reason: values.reason,
        date_to: extraBanData
      });
      showSuccessNotify();
      onBan({
        reason: values.reason,
        date_to: extraBanData,
        date_from: moment().format()
      });
    } catch (err) {
      showErrorNotify(err);
      setError(true);
    } finally {
      setFetching(false);
    }
  };

  return (
    <>
      <Form
        initialValues={{
          permanent: banData?.date_to === null,
          reason: banData?.reason || '',
          hour: '00:00:00',
          timezone: '+0000'
        }}
        validate={banUserFieldsValidator}
        mutators={{ cleanDate }}
        onSubmit={banUserHandle}>
        {({ form, handleSubmit, errors, submitFailed, values: { permanent } }) => {
          return (
            <>
              <FieldGroup label={intl.messages['users.list-block.table.ban-modal.period.label']}>
                <FlexParentBlock>
                  <Field name="date">
                    {({ input, meta }) => {
                      return (
                        <Datepicker
                          disabled={permanent}
                          errorMessage={
                            (meta.touched || submitFailed) &&
                            errors &&
                            errors.date &&
                            intl.formatMessage({ id: errors.date })
                          }
                          options={{
                            disabledDays: {
                              from: new Date(0),
                              to: new Date(moment().format())
                            }
                          }}
                          placeholder="Select date"
                          {...input}
                        />
                      );
                    }}
                  </Field>
                  <DateSeparator>
                    {intl.formatMessage({
                      id: 'users.list-block.table.ban-modal.period.separator'
                    })}
                  </DateSeparator>
                  <HoursSelectWrapper>
                    <FieldSelect name="hour" size="sm" disabled={permanent} options={hoursSelect} />
                  </HoursSelectWrapper>
                  <HoursSelectWrapper>
                    <FieldSelect
                      name="timezone"
                      size="sm"
                      disabled={permanent}
                      options={timezoneSelect}
                    />
                  </HoursSelectWrapper>
                </FlexParentBlock>
              </FieldGroup>
              <Field name="permanent" type="checkbox">
                {({ input }) => (
                  <CheckboxWrapper>
                    <Checkbox
                      checked={input.checked}
                      onChange={(event) => {
                        if (event.target.checked) {
                          form.mutators.cleanDate();
                        }
                        input.onChange(event);
                      }}>
                      {intl.messages['users.list-block.table.ban-modal.permanent']}
                    </Checkbox>
                  </CheckboxWrapper>
                )}
              </Field>
              <FieldTextarea
                name="reason"
                errorMessage={
                  errors &&
                  errors.reason &&
                  intl.formatMessage(
                    { id: errors.reason },
                    {
                      number: maxTextLength
                    }
                  )
                }
                resize
                label={intl.messages['users.list-block.table.ban-modal.reason.label']}
                sublabel={intl.messages['users.list-block.table.ban-modal.reason.description']}
              />
              <FormFooter
                isModal
                onSubmit={handleSubmit}
                onCancel={onClose}
                fetching={fetching}
                error={error}
                submitText={intl.formatMessage({
                  id: `users.list-block.table.ban-modal.reason.submit.ban`
                })}
                submitButtonPalette="danger"
                data-test-id="user-ban-modal-footer"
              />
            </>
          );
        }}
      </Form>
    </>
  );
};
